import moment from "moment";

import {
  LiveLectureAccessModeEnum,
  LiveLectureStatusEnum,
  RoleEnum,
} from "../../enums";
import { LiveLectureDto } from "../../interfaces";

export type LiveLectureManagerProfile = {
  id?: string;
  role?: { key?: string };
};

const LIVE_LECTURE_JOIN_ROLES = [
  RoleEnum.SUPER_ADMIN,
  RoleEnum.ADMIN,
  RoleEnum.SYSTEM_ADMIN,
  RoleEnum.HEAD_TEACHER_ADMIN,
  RoleEnum.TEACHER_ADMIN,
];

export const canJoinLiveLecture = (
  profile: LiveLectureManagerProfile | null | undefined,
): boolean =>
  LIVE_LECTURE_JOIN_ROLES.includes(profile?.role?.key as RoleEnum);

export const canManageLiveLecture = (
  lecture: Pick<LiveLectureDto, "teacherUserId" | "accessMode"> | null | undefined,
  profile: LiveLectureManagerProfile | null | undefined,
): boolean => {
  if (!lecture || !profile?.id) return false;
  if (
    lecture.accessMode === LiveLectureAccessModeEnum.PRIVATE &&
    profile.role?.key !== RoleEnum.SUPER_ADMIN
  ) {
    return false;
  }
  if (
    profile.role?.key === RoleEnum.SUPER_ADMIN ||
    profile.role?.key === RoleEnum.SYSTEM_ADMIN ||
    profile.role?.key === RoleEnum.HEAD_TEACHER_ADMIN ||
    profile.role?.key === RoleEnum.ADMIN
  )
    return true;
  return lecture.teacherUserId === profile.id;
};

export const canEditLiveLecture = (
  lecture:
    | Pick<LiveLectureDto, "status" | "endAt">
    | null
    | undefined,
) => {
  if (!lecture) return false;
  if (lecture.status === LiveLectureStatusEnum.DRAFT) return true;
  return !isPastLiveLecture(lecture.endAt);
};

export const isPastLiveLecture = (
  endAt: Date | string,
  now = Date.now(),
) => new Date(endAt).getTime() < now;

export const isOngoingLiveLecture = (
  startAt: Date | string,
  endAt: Date | string,
  now = Date.now(),
) => {
  const start = new Date(startAt).getTime();
  const end = new Date(endAt).getTime();
  return start <= now && end >= now;
};

export const isPublishedLiveLecture = (status: LiveLectureStatusEnum) =>
  [
    LiveLectureStatusEnum.PUBLISHED,
    LiveLectureStatusEnum.FULL,
    LiveLectureStatusEnum.IN_PROGRESS,
    LiveLectureStatusEnum.COMPLETED,
  ].includes(status);

export const LIVE_LECTURE_STATUS_COLORS: Record<
  LiveLectureStatusEnum,
  { backgroundColor: string; borderColor: string }
> = {
  [LiveLectureStatusEnum.DRAFT]: {
    backgroundColor: "#f7b84b",
    borderColor: "#e9a53a",
  },
  [LiveLectureStatusEnum.PUBLISHED]: {
    backgroundColor: "#0ab39c",
    borderColor: "#099885",
  },
  [LiveLectureStatusEnum.FULL]: {
    backgroundColor: "#f06548",
    borderColor: "#d95a3f",
  },
  [LiveLectureStatusEnum.IN_PROGRESS]: {
    backgroundColor: "#299cdb",
    borderColor: "#238bc5",
  },
  [LiveLectureStatusEnum.COMPLETED]: {
    backgroundColor: "#878a99",
    borderColor: "#76788f",
  },
  [LiveLectureStatusEnum.CANCELLED]: {
    backgroundColor: "#adb5bd",
    borderColor: "#98a2ac",
  },
};

export const LIVE_LECTURE_LEGEND_STATUSES = [
  LiveLectureStatusEnum.DRAFT,
  LiveLectureStatusEnum.PUBLISHED,
  LiveLectureStatusEnum.FULL,
  LiveLectureStatusEnum.IN_PROGRESS,
  LiveLectureStatusEnum.COMPLETED,
  LiveLectureStatusEnum.CANCELLED,
] as const;

export const getLiveLectureEventColor = (
  status: LiveLectureStatusEnum,
  accessMode?: LiveLectureAccessModeEnum,
) => {
  const colors =
    LIVE_LECTURE_STATUS_COLORS[status] ?? {
      backgroundColor: "#405189",
      borderColor: "#364574",
    };

  if (accessMode === LiveLectureAccessModeEnum.SUBJECT_INCLUDED) {
    return {
      ...colors,
      borderColor: "#50a5f1",
      borderStyle: "dashed" as const,
    };
  }

  return colors;
};

export const getLiveLectureStatusDotColor = (status: LiveLectureStatusEnum) =>
  getLiveLectureEventColor(status).backgroundColor;

export const formatLiveLectureDate = (startAt: Date | string) =>
  moment(startAt).format("ll");

export const formatLiveLectureTimeSlot = (
  startAt: Date | string,
  endAt: Date | string,
) =>
  `${moment(startAt).format("h:mm A")} - ${moment(endAt).format("h:mm A")}`;

const stripHtml = (value?: string) =>
  value?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() ?? "";

export const buildLiveLectureEventSummary = (
  lecture: LiveLectureDto,
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string,
) => {
  const title = language === "ar" ? lecture.title.ar : lecture.title.en;
  const teacherName = lecture.teacher
    ? language === "ar"
      ? lecture.teacher.name.ar
      : lecture.teacher.name.en
    : "";
  const subjectName = lecture.subject
    ? language === "ar"
      ? lecture.subject.name?.ar
      : lecture.subject.name?.en
    : "";
  const description = stripHtml(
    language === "ar"
      ? lecture.description?.ar
      : lecture.description?.en,
  );
  const capacityLabel =
    lecture.accessMode === LiveLectureAccessModeEnum.SUBJECT_INCLUDED
      ? ""
      : lecture.maxParticipants !== undefined
        ? `${lecture.bookedCount}/${lecture.maxParticipants}`
        : `${lecture.bookedCount}`;

  return [
    title,
    `${t("liveLectureAccessMode")}: ${t(`liveLectureAccessMode_${lecture.accessMode}`)}`,
    `${t("status")}: ${t(`liveLectureStatus_${lecture.status}`)}`,
    lecture.type
      ? `${t("liveLectureType")}: ${t(`liveLectureType_${lecture.type}`)}`
      : "",
    `${formatLiveLectureDate(lecture.startAt)} · ${formatLiveLectureTimeSlot(lecture.startAt, lecture.endAt)}`,
    teacherName ? `${t("teacher")}: ${teacherName}` : "",
    subjectName ? `${t("subject")}: ${subjectName}` : "",
    capacityLabel ? `${t("bookedCount")}: ${capacityLabel}` : "",
    lecture.accessMode === LiveLectureAccessModeEnum.STANDALONE_PAID
      ? `${t("price")}: ${lecture.price} ${lecture.currency}`
      : "",
    description
      ? `${t("description")}: ${description.slice(0, 120)}${description.length > 120 ? "..." : ""}`
      : "",
  ]
    .filter(Boolean)
    .join("\n");
};
